import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { BoardRow, CrmAuth, SalesOverviewData } from './crmApi';
import { HolidayBanner } from './Holidays';
import LeadPanel from './LeadPanel';
import { Empty, Spinner, fmtPhone, fmtWhen, useCrmMe } from './ui';
import { C, Card, Columns, LevelBadge, ReasonBars, Ring, Tile, dayLabel, deltaOf, greeting, nf } from './viz';

// ── Sales Overview: the Sales Admin's home ───────────────────
// The team's conversions this week, the leaderboard, eight weeks of
// conversions, who is closest to paying and why people said no.
// Mirrors the app's CrmSalesOverviewScreen.

const MEDAL = ['🥇', '🥈', '🥉'];
const PODIUM_BG = ['linear-gradient(160deg,#fff8e1,#fde68a)', 'linear-gradient(160deg,#f8fafc,#e2e8f0)', 'linear-gradient(160deg,#fff7ed,#fed7aa)'];

const Podium: React.FC<{ rows: BoardRow[] }> = ({ rows }) => {
  // 2nd · 1st · 3rd, the winner in the middle and tallest
  const order = [rows[1], rows[0], rows[2]].filter(Boolean) as BoardRow[];
  const lift: Record<number, string> = { 1: 'sm:mt-0', 2: 'sm:mt-6', 3: 'sm:mt-10' };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
      {order.map(r => (
        <div key={r.id} className={`rounded-2xl p-4 text-center border border-black/5 ${lift[r.rank] || ''}`} style={{ background: PODIUM_BG[r.rank - 1] }}>
          <p className="text-3xl leading-none" aria-label={`Rank ${r.rank}`}>{MEDAL[r.rank - 1]}</p>
          <p className="font-bold text-gray-900 mt-1 truncate">{r.name}</p>
          <p className="text-3xl font-bold mt-1" style={{ color: C.ink }}>{r.converted}</p>
          <p className="text-[11px] text-gray-600">converted · {r.pct}% rate</p>
          <div className="mt-2"><LevelBadge level={r.level} small /></div>
        </div>
      ))}
    </div>
  );
};

const SalesOverview: React.FC<{ auth: CrmAuth; name: string; onNavigate: (page: string) => void }> = ({ auth, name, onNavigate }) => {
  const { me } = useCrmMe(auth);
  const [d, setD] = useState<SalesOverviewData | null>(null);
  const [failed, setFailed] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setD(await crm.salesOverview(auth)); setFailed(''); } catch (e: any) { setFailed(e.message); toast.error(e.message); }
  }, [auth]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 120000);
    return () => clearInterval(t);
  }, [load]);

  if (!d) return failed ? <Empty icon="⚠️" title="Could not load the sales overview" sub={failed} /> : <Spinner label="Loading the sales overview…" />;
  const w = d.thisWeek, last = d.lastWeek;
  const conv = deltaOf(w.converted, last.converted);
  const topConv = Math.max(1, ...d.board.map(b => b.converted));
  const anyConverted = d.board.some(b => b.converted > 0);

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-3xl p-5 sm:p-7 text-white" style={{ background: 'linear-gradient(135deg,#1a1428 0%,#2b2240 45%,#4e3a69 100%)' }}>
        <div className="absolute -top-20 -left-10 w-72 h-72 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(240,180,41,0.25), transparent 70%)' }} />
        <div className="absolute -bottom-24 right-0 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,101,53,0.2), transparent 70%)' }} />
        <div className="relative flex flex-col lg:flex-row items-center gap-6">
          <div className="flex-1 min-w-0 text-center lg:text-left">
            <p className="text-white/60 text-sm">{greeting()}, {name.split(' ')[0]} 👋</p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1">Sales this week · {dayLabel(d.week.from)} – {dayLabel(d.week.to)}</h1>
            <div className="mt-4 flex items-end justify-center lg:justify-start gap-3">
              <p className="text-6xl font-bold leading-none">{nf(w.converted)}</p>
              <div className="pb-1 text-left">
                <p className="text-sm font-bold">converted</p>
                <p className={`text-xs font-bold ${conv.good === null ? 'text-white/60' : conv.good ? 'text-green-300' : 'text-red-300'}`}>{conv.text}</p>
              </div>
            </div>
            <p className="text-white/70 text-sm mt-3">{nf(w.pitched)} brochures · {nf(w.pollsSent ?? 0)} polls · {nf(w.interested ?? 0)} interested · {nf(w.calls)} calls</p>
          </div>
          <Ring pct={w.pct} color={C.gold} track={C.goldTrack} label={`Team conversion rate ${w.pct}%`}>
            <p className="text-5xl font-bold leading-none">{w.pct}%</p>
            <p className="text-[10px] uppercase tracking-widest text-white/60 mt-1.5">team conversion</p>
            <p className="text-xs text-white/80 mt-0.5">{d.teamLevel.emoji} {d.teamLevel.name} level</p>
          </Ring>
        </div>
      </div>

      <HolidayBanner note={d.holiday} manager onOpen={() => onNavigate('team:holidays')} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Conversion rate" value={`${w.pct}%`} delta={deltaOf(w.pct, last.pct, ' pts')} sub="converted of those sent a brochure" />
        <Tile label="Waiting for payment" value={nf(d.pipeline.count)} sub="said Interested" />
        <Tile label="Visits" value={nf(d.visits)} sub="today and tomorrow" />
        <Tile label="Polls to update" value={nf(d.resultsPending.total)} sub={d.resultsPending.overdue ? `${d.resultsPending.overdue} due now` : 'waiting for their status'} />
      </div>

      <Card title="Leaderboard" sub="This week, by conversions, then conversion rate">
        {d.board.length === 0 ? <p className="text-sm text-gray-400">Nobody is taking leads yet.</p> : (
          <div className="space-y-4">
            {anyConverted && <Podium rows={d.board.slice(0, 3).filter(r => r.converted > 0)} />}
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm min-w-[640px]">
                <thead><tr className="text-xs text-gray-500">
                  {['#', 'Salesperson', 'Level', 'Converted', 'Conversion rate', 'Brochures', 'Calls'].map((h, i) => (
                    <th key={h} className={`py-2 px-2 font-bold ${i < 3 ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {d.board.map(r => (
                    <tr key={r.id} className="border-t border-gray-100">
                      <td className="py-2 px-2 w-10">{(r.converted > 0 && MEDAL[r.rank - 1]) || <span className="text-xs font-bold text-gray-400">{r.rank}</span>}</td>
                      <td className="py-2 px-2 font-semibold text-gray-800">{r.name}</td>
                      <td className="py-2 px-2"><LevelBadge level={r.level} small /></td>
                      <td className="py-2 px-2 text-right">
                        <span className="inline-flex items-center gap-2 justify-end">
                          <span className="hidden sm:block w-20 h-1.5 rounded-r" style={{ background: C.track }}>
                            <span className="block h-1.5 rounded-r" style={{ width: `${r.converted ? Math.max(4, (r.converted / topConv) * 100) : 0}%`, background: C.leads }} />
                          </span>
                          <b className="tabular-nums text-gray-900">{r.converted}</b>
                        </span>
                        <span className={`block text-[10px] ${r.converted > r.lastConverted ? 'text-green-700' : r.converted < r.lastConverted ? 'text-red-600' : 'text-gray-400'}`}>last week {r.lastConverted}</span>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.base ? `${r.pct}%` : '—'}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.pitched}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.calls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card title="Conversions, week by week" sub="The team, last 8 weeks" className="lg:col-span-3">
          <Columns data={d.weeks.map(x => ({ key: x.week, label: dayLabel(x.week), value: x.converted, tip: `${nf(x.converted)} converted · ${x.pct}% rate` }))}
            ariaLabel="Team conversions per week, last 8 weeks" tableHead={['Week of', 'Converted · rate']} />
        </Card>
        <Card title="Why they said no" sub="Not Interested in the poll, this week" className="lg:col-span-2">
          <ReasonBars rows={d.lost} empty="No Not Interested this week" />
        </Card>
      </div>

      <Card title="Closest to paying" sub="Interested, waiting to pay: the ones with a payment date first"
        right={<button onClick={() => onNavigate('dashboard')} className="text-xs font-bold text-purple-600 whitespace-nowrap">Full dashboard →</button>}>
        {d.pipeline.leads.length === 0 ? <p className="text-sm text-gray-400">Nobody is waiting to pay.</p> : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 divide-y md:divide-y-0 divide-gray-100">
            {d.pipeline.leads.map(l => (
              <li key={l.id} className="md:border-b md:border-gray-100">
                <button onClick={() => setOpen(l.id)} className="w-full text-left py-2 flex items-center gap-3 hover:bg-purple-50/40 rounded-lg px-1">
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{l.name || fmtPhone(l.phone)}</span>
                    <span className="block text-[11px] text-gray-400 truncate">{[l.ownerName, l.course].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className={`text-xs font-bold whitespace-nowrap ${l.nextAt && new Date(l.nextAt).getTime() < Date.now() ? 'text-red-600' : 'text-gray-700'}`}>{fmtWhen(l.nextAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

export default SalesOverview;
